import { Chip, TableCell, TableRow, Button, Switch } from '@mui/material';
import * as React from 'react';
import { RecipientPayload } from './NewsApi';
import { getRoleLabel, formatSafe, NewsItemStatus } from './NewsUtils';
import { getNewsDisplayStatus, NewsDisplayStatus } from './newsDisplayStatus';

export type INewsItem = {
    id: number;
    title: string;
    content: string;
    visibleDate: string;
    startDate: string;
    endDate: string;
    recipients: RecipientPayload[];
    visible: boolean;
    status?: number;
    readCount?: number;
};

export type NewsStatusLabels = { [status in NewsDisplayStatus]: string };

export interface INewsItemProps {
    news: INewsItem;
    onEdit: (id: number) => void;
    editButtonLabel: string;
    onToggleVisible?: (id: number) => void;
    statusLabels: NewsStatusLabels;
    today: string;
}

const statusColors: {
    [status in NewsDisplayStatus]: 'default' | 'info' | 'success' | 'warning';
} = {
    [NewsDisplayStatus.Draft]: 'default',
    [NewsDisplayStatus.Scheduled]: 'info',
    [NewsDisplayStatus.Live]: 'success',
    [NewsDisplayStatus.Expired]: 'warning',
    [NewsDisplayStatus.Archived]: 'default'
};

export default function NewsItem({
    news,
    onEdit,
    onToggleVisible,
    editButtonLabel,
    statusLabels,
    today
}: INewsItemProps) {
    const handleToggleSwitch = () => {
        if (onToggleVisible) {
            onToggleVisible(news.id);
        }
    };

    const isArchived = news.status === NewsItemStatus.Archived;
    const displayStatus = getNewsDisplayStatus(news, today);
    const cellSx = { opacity: isArchived ? 0.6 : 1 };

    return (
        <TableRow sx={isArchived ? { backgroundColor: 'grey.100' } : {}}>
            <TableCell
                sx={{
                    width: '8px',
                    backgroundColor: isArchived ? 'grey.400' : 'primary.main',
                    padding: 0,
                    borderBottom: 'none'
                }}
            />
            <TableCell sx={cellSx}>{news.title || '–'}</TableCell>
            <TableCell sx={cellSx}>
                <Chip
                    size="small"
                    label={statusLabels[displayStatus]}
                    color={statusColors[displayStatus]}
                    variant={displayStatus === NewsDisplayStatus.Live ? 'filled' : 'outlined'}
                />
            </TableCell>
            <TableCell sx={cellSx}>{formatSafe(news.startDate)}</TableCell>
            <TableCell sx={cellSx}>{formatSafe(news.endDate)}</TableCell>
            <TableCell sx={{ ...cellSx, color: 'secondary.dark' }}>
                {formatSafe(news.visibleDate)}
            </TableCell>
            <TableCell sx={cellSx}>
                {Array.isArray(news.recipients) && news.recipients.length > 0 ? (
                    (() => {
                        const uniqueByRole = Array.from(
                            new Map(news.recipients.map((r) => [r.role, r])).values()
                        );
                        return uniqueByRole.map((recipient, index) => (
                            <Chip
                                key={`${recipient.role}-${index}`}
                                label={getRoleLabel(recipient.role)}
                                sx={{ mr: 0.5, mb: 0.5 }}
                            />
                        ));
                    })()
                ) : (
                    <span style={{ color: '#999' }}>–</span>
                )}
            </TableCell>
            <TableCell sx={cellSx}>{news.readCount || 0}</TableCell>
            <TableCell align="left" sx={cellSx}>
                <Switch
                    checked={news.visible}
                    onChange={handleToggleSwitch}
                    disabled={isArchived}
                />
            </TableCell>
            <TableCell>
                <Button
                    variant="contained"
                    size="medium"
                    color="primary"
                    onClick={() => onEdit(news.id)}
                    sx={{ textTransform: 'none', borderRadius: 0 }}
                >
                    {editButtonLabel}
                </Button>
            </TableCell>
        </TableRow>
    );
}
